import { Box, Circle, Divider, Flex, Image, Text } from '@chakra-ui/react';
import { useAppSelector } from '../../../../../store/redux/hooks';
import { useImageBrightness } from '../../hooks/useImageBrightness';
import {
  PosterActionCard as ActionCard,
  type PosterCard,
} from './PosterActionCard';
import {
  POSTER_CARD_BG as CARD_BG,
  POSTER_INFO_BAR_BG_DARK as CARD_INFO_BAR_BG_DARK,
  POSTER_INFO_BAR_BG_LIGHT as CARD_INFO_BAR_BG_LIGHT,
  POSTER_FACE_ASPECT_RATIO,
  POSTER_FACE_INSET,
} from './posterCardStyles';
import { ImagePlaceholder } from '../shared/ImagePlaceholder';
import type { PosterImages } from '../../../../../store/types/template';

export interface PosterData {
  event?: string;
  date?: string;
  time?: string;
  info?: string;
  section?: string;
  row?: string;
  seat?: string;
  venue?: string;
}

interface ApplePosterPreviewProps {
  images?: PosterImages;
  data?: PosterData;
  cards?: [PosterCard, PosterCard, PosterCard];
  artworkBrightness?: 'light' | 'dark' | null;
}

const DEFAULT_CARDS: [PosterCard, PosterCard, PosterCard] = [
  {
    label: 'Additional Ticket Info',
    subtitle: 'More',
    center: true,
    hideIcon: true,
  },
  { label: '<venue>', subtitle: 'Open in Maps', center: true },
  { label: '<event>', subtitle: 'Event Guide', center: true },
];

const SeatColumn = ({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color: string;
}) => (
  <Box textAlign="center">
    <Text
      fontSize="10px"
      color={color}
      fontWeight="500"
      lineHeight="1.2"
      letterSpacing="-0.2px"
    >
      {label}
    </Text>
    <Text
      fontSize="11px"
      color={color}
      fontWeight="600"
      lineHeight="1.2"
      letterSpacing="-0.2px"
      mt="2px"
    >
      {value}
    </Text>
  </Box>
);

const ApplePosterPreview = ({
  images,
  data,
  cards,
  artworkBrightness,
}: ApplePosterPreviewProps) => {
  const reduxImages = useAppSelector(
    (s) => s.template.appleEnhancedDesign.images
  );
  const reduxBrightness = useAppSelector(
    (s) => s.template.appleEnhancedDesign.artworkBrightness
  );

  const artwork = images?.artwork ?? reduxImages.artwork;
  const secondaryLogo = images?.secondaryLogo ?? reduxImages.secondaryLogo;
  const storedBrightness = images
    ? (artworkBrightness ?? null)
    : reduxBrightness;
  const liveBrightness = useImageBrightness(storedBrightness ? null : artwork);
  const brightness = storedBrightness ?? liveBrightness;
  const isLightArtwork = brightness === 'light';
  const passTextColor = isLightArtwork ? 'gray.900' : 'white';
  const infoBarBg = isLightArtwork
    ? CARD_INFO_BAR_BG_LIGHT
    : CARD_INFO_BAR_BG_DARK;

  const [topCard, leftCard, rightCard] = cards ?? DEFAULT_CARDS;

  return (
    <>
      <Box
        position="absolute"
        inset={POSTER_FACE_INSET}
        aspectRatio={POSTER_FACE_ASPECT_RATIO}
        bg={CARD_BG}
        borderRadius="20px"
        overflow="hidden"
      >
        {artwork ? (
          <Image
            src={artwork}
            alt=""
            position="absolute"
            inset="0"
            w="100%"
            h="100%"
            objectFit="cover"
          />
        ) : (
          <ImagePlaceholder position="absolute" inset="0" w="100%" h="100%" />
        )}

        <Box
          position="absolute"
          top="0"
          left="50%"
          transform="translateX(-50%)"
          w="70px"
          h="15px"
          overflow="hidden"
          zIndex={2}
          pointerEvents="none"
        >
          <Circle
            bg="#F3F2FA"
            size="70px"
            position="absolute"
            bottom="0"
            border="1px solid #E0E0E6"
          />
        </Box>

        <Box position="absolute" top="14px" left="14px" zIndex={1}>
          <Text
            fontSize="11px"
            color={passTextColor}
            fontWeight="600"
            lineHeight="1.2"
            letterSpacing="-0.2px"
            whiteSpace="pre-line"
          >
            {data?.event ?? '<event>'}
          </Text>
        </Box>
        <Box
          position="absolute"
          top="14px"
          right="14px"
          textAlign="right"
          zIndex={1}
        >
          <Text
            fontSize="10px"
            color={passTextColor}
            fontWeight="600"
            lineHeight="1.2"
            letterSpacing="-0.2px"
          >
            {data?.date ?? '<date>'}
          </Text>
          <Text
            fontSize="9px"
            color={passTextColor}
            fontWeight="400"
            lineHeight="1.2"
            letterSpacing="-0.2px"
          >
            {data?.time ?? '<time>'}
          </Text>
        </Box>

        <Box
          position="absolute"
          bottom="0"
          left="0"
          right="0"
          h="29%"
          borderBottomLeftRadius="20px"
          borderBottomRightRadius="20px"
          overflow="hidden"
          zIndex={1}
        >
          <Box
            position="absolute"
            inset="0"
            bg={infoBarBg}
            borderBottomLeftRadius="20px"
            borderBottomRightRadius="20px"
            sx={{
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            }}
          />

          <Flex
            position="absolute"
            left="14px"
            right="14px"
            top="42%"
            transform="translateY(-50%)"
            justify="space-between"
            align="center"
          >
            <SeatColumn
              label="Gate"
              value={data?.info ?? '<gate>'}
              color={passTextColor}
            />
            <SeatColumn
              label="Sec"
              value={data?.section ?? '<section>'}
              color={passTextColor}
            />
            <SeatColumn
              label="Row"
              value={data?.row ?? '<row>'}
              color={passTextColor}
            />
            <SeatColumn
              label="Seat"
              value={data?.seat ?? '<seat>'}
              color={passTextColor}
            />
          </Flex>

          <Box position="absolute" left="14px" bottom="8px">
            <Text
              fontSize="9px"
              color={passTextColor}
              fontWeight="500"
              lineHeight="1.2"
              letterSpacing="-0.2px"
            >
              {data?.venue ?? '<venue>'}
            </Text>
          </Box>

          <Box position="absolute" right="14px" bottom="8px">
            {secondaryLogo ? (
              <Image
                src={secondaryLogo}
                alt=""
                maxH="14px"
                maxW="110px"
                objectFit="contain"
                objectPosition="right"
              />
            ) : (
              <Text
                fontSize="8px"
                color={passTextColor}
                fontWeight="500"
                lineHeight="1.2"
                letterSpacing="-0.2px"
              >
                Logo 2
              </Text>
            )}
          </Box>
        </Box>
      </Box>

      <Box position="absolute" top="68%" left="7%" right="7%" bottom="12%">
        <Flex direction="column" gap="6px" h="100%">
          <Box flex={0.7}>
            <ActionCard {...topCard} />
          </Box>
          <Flex gap="6px" flex={1.3}>
            <ActionCard {...leftCard} />
            <ActionCard {...rightCard} />
          </Flex>
        </Flex>
      </Box>

      <Flex
        position="absolute"
        inset="90% 10% auto 0"
        my={3}
        width="full"
        justifyContent="center"
        direction="row"
        align="center"
      >
        <Divider
          orientation="vertical"
          borderColor="black"
          w="25%"
          borderWidth="2px"
          borderRadius="25px"
        />
      </Flex>
    </>
  );
};

export default ApplePosterPreview;
